// src/components/Admin/Customer_Master/CustomerAddEdit.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Save,
  User,
  Phone,
  Mail,
  MapPin,
  StickyNote,
  Calendar,
  Gift,
  Sparkles,
  CreditCard,
  UtensilsCrossed,
  UserCircle,
} from "lucide-react";

// Moved outside to prevent re-creation on every render (fixes focus loss issue)
const InputField = ({ label, icon: Icon, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {children}
      <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  </div>
);

const inputClass = "w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 transition-all";
const selectClass = "w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 transition-all appearance-none";

export default function CustomerAddEdit({ onSave, onClose, item }) {
  // Initialize formData with exact backend keys and keep in sync when editing
  const [formData, setFormData] = useState(
    item ?? {
      id: 0,
      name: "",
      phone: "",
      email: "",
      address: "",
      totalVisits: 0,
      totalSpent: 0,
      notes: "",
      dateOfBirth: "",
      anniversaryDate: "",
      customerType: "regular",
      loyaltyPoints: 0,
      preferredPaymentMode: "",
      dietaryPreferences: "",
      isActive: true,
    }
  );

  // Keep form in sync if `item` prop changes (edit mode)
  useEffect(() => {
    if (item) {
      setFormData({
        id: item.id ?? 0,
        name: item.name ?? "",
        phone: item.phone ?? "",
        email: item.email ?? "",
        address: item.address ?? "",
        totalVisits: item.totalVisits ?? 0,
        totalSpent: item.totalSpent ?? 0,
        notes: item.notes ?? "",
        dateOfBirth: item.dateOfBirth ?? "",
        anniversaryDate: item.anniversaryDate ?? "",
        customerType: item.customerType ?? "regular",
        loyaltyPoints: item.loyaltyPoints ?? 0,
        preferredPaymentMode: item.preferredPaymentMode ?? "",
        dietaryPreferences: item.dietaryPreferences ?? "",
        isActive: item.isActive ?? true,
      });
    }
  }, [item]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      alert("Customer name is required");
      return;
    }

    if (!String(formData.phone || "").trim()) {
      alert("Phone is required");
      return;
    }

    // Build clean payload matching backend expectations
    const payload = {
      id: formData.id ?? 0,
      name: (formData.name || "").trim(),
      phone: (formData.phone || "").trim(),
      email: (formData.email || "").trim() || undefined,
      address: (formData.address || "").trim() || undefined,
      totalVisits:
        formData.totalVisits !== undefined
          ? Number(formData.totalVisits) || 0
          : 0,
      totalSpent:
        formData.totalSpent !== undefined
          ? Number(formData.totalSpent) || 0
          : 0,
      notes: formData.notes ?? "",
      dateOfBirth: formData.dateOfBirth || undefined,
      anniversaryDate: formData.anniversaryDate || undefined,
      customerType: formData.customerType || "regular",
      loyaltyPoints:
        formData.loyaltyPoints !== undefined
          ? Number(formData.loyaltyPoints) || 0
          : 0,
      preferredPaymentMode: formData.preferredPaymentMode || undefined,
      dietaryPreferences: formData.dietaryPreferences || "",
      isActive:
        formData.isActive !== undefined ? !!formData.isActive : true,
    };

    // For create, many backends expect POST without an id property
    if (!payload.id) delete payload.id;

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <UserCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">
                  {item ? "Edit Customer" : "Add New Customer"}
                </h2>
                <p className="text-xs text-teal-100">
                  {item ? "Update customer information" : "Create a new customer profile"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Form body - Two Column Layout */}
        <form onSubmit={handleSubmit}>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-5">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Primary Info Card */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <User className="w-3.5 h-3.5" /> Contact Info
                  </h4>

                  <InputField label="Name" icon={User} required>
                    <input
                      type="text"
                      value={formData.name || ""}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Customer name"
                      className={inputClass}
                      required
                    />
                  </InputField>

                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Phone" icon={Phone} required>
                      <input
                        type="tel"
                        value={formData.phone || ""}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="Phone number"
                        className={inputClass}
                        required
                      />
                    </InputField>

                    <InputField label="Email" icon={Mail}>
                      <input
                        type="email"
                        value={formData.email || ""}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="Email address"
                        className={inputClass}
                      />
                    </InputField>
                  </div>

                  <InputField label="Address" icon={MapPin}>
                    <textarea
                      value={formData.address || ""}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder="Full address"
                      rows={2}
                      className={`${inputClass} resize-none pt-2`}
                    />
                  </InputField>
                </div>

                {/* Notes Card */}
                <div className="bg-amber-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-2">
                    <StickyNote className="w-3.5 h-3.5" /> Notes
                  </h4>
                  <textarea
                    value={formData.notes || ""}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder="Special notes about this customer..."
                    rows={2}
                    className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all resize-none"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Customer Details Card */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Customer Details
                  </h4>

                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Customer Type" icon={Sparkles}>
                      <select
                        value={formData.customerType || "regular"}
                        onChange={(e) => handleChange("customerType", e.target.value)}
                        className={selectClass}
                      >
                        <option value="regular">Regular</option>
                        <option value="vip">VIP</option>
                        <option value="corporate">Corporate</option>
                      </select>
                    </InputField>

                    <InputField label="Loyalty Points" icon={Sparkles}>
                      <input
                        type="number"
                        min="0"
                        value={formData.loyaltyPoints ?? 0}
                        onChange={(e) => handleChange("loyaltyPoints", e.target.value)}
                        className={inputClass}
                      />
                    </InputField>

                    <InputField label="Payment Mode" icon={CreditCard}>
                      <select
                        value={formData.preferredPaymentMode || ""}
                        onChange={(e) => handleChange("preferredPaymentMode", e.target.value)}
                        className={selectClass}
                      >
                        <option value="">Select</option>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                        <option value="wallet">Wallet</option>
                        <option value="credit">Credit</option>
                      </select>
                    </InputField>

                    <InputField label="Dietary Pref." icon={UtensilsCrossed}>
                      <input
                        type="text"
                        value={formData.dietaryPreferences || ""}
                        onChange={(e) => handleChange("dietaryPreferences", e.target.value)}
                        placeholder="Veg, Allergies..."
                        className={inputClass}
                      />
                    </InputField>
                  </div>
                </div>

                {/* Important Dates Card */}
                <div className="bg-purple-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" /> Important Dates
                  </h4>

                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Date of Birth" icon={Calendar}>
                      <input
                        type="date"
                        value={formData.dateOfBirth || ""}
                        onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                        className={inputClass}
                      />
                    </InputField>

                    <InputField label="Anniversary" icon={Gift}>
                      <input
                        type="date"
                        value={formData.anniversaryDate || ""}
                        onChange={(e) => handleChange("anniversaryDate", e.target.value)}
                        className={inputClass}
                      />
                    </InputField>
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Active Status</p>
                      <p className="text-xs text-gray-500">Customer can be linked to orders</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleChange("isActive", !formData.isActive)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        formData.isActive ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          formData.isActive ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-all flex items-center gap-2 shadow-sm"
            >
              <Save className="w-4 h-4" />
              {item ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
